/**
 * Shared CSV writer for every export in the planner (orders, routes, stats,
 * driver stats).
 *
 * Reported by the customer (Aug 3, 2026) on an orders export opened in Excel:
 * accented text came out as mojibake (`Marché` → `March√©`, `Genève` →
 * `Gen√®ve`) and single orders were split across several spreadsheet rows.
 * The file itself was valid RFC-4180 — 43 rows × 44 fields, none malformed —
 * so both problems were Excel interop, and both are fixed here rather than in
 * four separate copies:
 *
 *   1. **UTF-8 BOM.** Excel does not sniff UTF-8; without a leading BOM it
 *      falls back to the system legacy encoding (MacRoman / Windows-1252) and
 *      mangles every accent. `charset=utf-8` in the Blob MIME type is ignored
 *      for a downloaded file.
 *   2. **CRLF line endings + no newline inside a cell.** The exports used LF
 *      and left the multi-line pickup notes intact; Excel then broke each note
 *      line into its own row. Quoted newlines are legal CSV, but Excel is not
 *      reliable about them, so cells are flattened to a single line — a note
 *      reads fine as one line in a spreadsheet, whereas a broken table does not.
 */

/**
 * Quote/escape one cell. Any CR/LF inside the value collapses to a space so a
 * cell can never break the row, in any consumer.
 */
export function escapeCsvField(value) {
  if (value == null) return '';
  const str = String(value).replace(/\r\n|\r|\n/g, ' ');
  if (str.includes(',') || str.includes('"')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

/** Join pre-escaped lines into an RFC-4180 document (CRLF-terminated). */
export function buildCsv(lines) {
  return lines.join('\r\n');
}

/**
 * Trigger the browser download. `﻿` is the UTF-8 BOM — it must be the
 * first character of the Blob, not of the string passed to the caller, so the
 * CSV content itself stays clean.
 */
export function downloadCsv(lines, filename) {
  const blob = new Blob(['﻿', buildCsv(lines)], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
